'use client'

import Link from 'next/link'
import { type ComponentPropsWithRef, type MouseEvent, type ReactNode } from 'react'

import { useNavigationActions } from '@/components/providers/NavigationProvider'
import { type AnalyticsEventType } from '@/defs/analytics'
import { type DistributiveOmit } from '@/defs/misc'
import { trackEvent } from '@/utils/analyticsUtil'
import { isModifiedClick } from '@/utils/commonUtil'

type LinkRouteProps = {
  analyticsEvent?: AnalyticsEventType
  children: ReactNode
  hash: string
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void
}

type Props = LinkRouteProps & DistributiveOmit<ComponentPropsWithRef<'a'>, keyof LinkRouteProps>

const focusTarget = (id: string) => {
  const target = document.getElementById(id)
  if (!target) return
  target.focus({ preventScroll: true })
}

export const LinkHash = ({ analyticsEvent, children, hash, onClick: onClickProp, ...props }: Props) => {
  const { setScrollRequest } = useNavigationActions()
  const href = `#${hash}`

  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    onClickProp?.(e)
    if (e.defaultPrevented) return
    if (analyticsEvent) trackEvent({ ...analyticsEvent, label: analyticsEvent.label || href })
    if (isModifiedClick(e)) return

    e.preventDefault()
    if (window.location.hash !== href) window.history.pushState(null, '', href)
    focusTarget(hash)
    setScrollRequest(href)
  }

  return (
    <Link href={href} onClick={onClick} prefetch={false} {...props}>
      {children}
    </Link>
  )
}
